// src/services/soundNotificationService.js

class SoundNotificationService {
  constructor() {
    this.storageKey = "semprejoias_sound_enabled";
    this.audioCtx = null;
  }

  // Inicialização sob demanda para respeitar políticas de autoplay dos navegadores
  getAudioContext() {
    if (typeof window === "undefined") return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    if (!this.audioCtx || this.audioCtx.state === "closed") {
      this.audioCtx = new AudioContextClass();
    }

    if (this.audioCtx.state === "suspended") {
      this.audioCtx.resume().catch(() => {});
    }

    return this.audioCtx;
  }

  // Verifica se o som está ativado nas preferências do usuário (padrão: ativado)
  isSoundEnabled() {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(this.storageKey);
    return stored === null ? true : stored === "true";
  }

  // Alterna o estado do som
  setSoundEnabled(enabled) {
    if (typeof window === "undefined") return;
    localStorage.setItem(this.storageKey, enabled ? "true" : "false");
  }

  toggleSound() {
    const nextState = !this.isSoundEnabled();
    this.setSoundEnabled(nextState);
    if (nextState) {
      this.playMessageSound();
    }
    return nextState;
  }

  // Som suave de nova mensagem no chat (acorde duplo "pop / chime" estilo Slack)
  playMessageSound() {
    if (!this.isSoundEnabled()) return;

    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;

      // Nota 1 (587.33 Hz - D5)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(587.33, now);
      gain1.gain.setValueAtTime(0.12, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.12);

      // Nota 2 (880.00 Hz - A5)
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(880.0, now + 0.08);
      gain2.gain.setValueAtTime(0.14, now + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.08);
      osc2.stop(now + 0.22);
    } catch (e) {
      console.warn("Não foi possível reproduzir som de mensagem:", e);
    }
  }

  // Som de notificação / menção / atribuição de tarefa (acorde triplo ascendente suave)
  playNotificationSound() {
    if (!this.isSoundEnabled()) return;

    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5

      notes.forEach((freq, index) => {
        const startTime = now + index * 0.07;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0.15, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(startTime);
        osc.stop(startTime + 0.2);
      });
    } catch (e) {
      console.warn("Não foi possível reproduzir som de notificação:", e);
    }
  }

  // Som para tarefas urgentes / alertas críticos
  playUrgentSound() {
    if (!this.isSoundEnabled()) return;

    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const notes = [698.46, 880.0, 1046.5]; // F5, A5, C6

      notes.forEach((freq, index) => {
        const startTime = now + index * 0.09;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0.18, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(startTime);
        osc.stop(startTime + 0.25);
      });
    } catch (e) {
      console.warn("Não foi possível reproduzir som urgente:", e);
    }
  }
}

export const soundNotificationService = new SoundNotificationService();
export default soundNotificationService;
